/* eslint-disable max-len */
import {
  useTranslate,
  FunctionField,
  ExportButton,
  useGetList,
  useList,
  ListContextProvider,
  List,
  useDataProvider,
  useShowContext
} from "react-admin";
import CustomDataGrid from "./CustomDataGrid";
import { useNavigate, useParams } from "react-router-dom";
import { DownloadIcon } from "assets/Icons";
import dayjs from "dayjs";
import { CustomButton } from "./CustomButton";
import { muiColors } from "themes/colors";
import ConfirmationModal from "./ConfirmationModal";
import { useState } from "react";
import AddArbitaryCreditsModal from "./AddArbitaryCreditsModal";
import { formatCurrency } from "utils/helpers";

const paymentMethodNames = {
  STRIPE: "Card on file",
  POS: "POS",
  CASH: "Cash",
  GIFTCARD: "Gift card",
  REFUND: "Merch credit"
};

export default function OrderTransactionsList({
  userCompanyId,
  customerUserId,
  deleteEnabled = false
}: {
  userCompanyId?: string;
  customerUserId?: string;
  deleteEnabled?: boolean;
}) {
  const { id } = useParams<{ id: string }>();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const { record } = useShowContext();
  const [isConfirmDeleteOrder, setIsConfirmDeleteOrder] =
    useState<boolean>(false);
  const [isRefundTransactionModalOpen, setIsRefundTransactionModalOpen] =
    useState<boolean>(false);
  const [refundTransactionId, setRefundTransactionId] = useState<string | null>(
    null
  );

  const isRefundableTransaction = (tx: any) =>
    ["STRIPE", "POS"].includes(tx?.payment_method) &&
    tx?.status === "SUCCESS" &&
    tx?.type === "CAPTURE" &&
    Boolean(tx?.charge_id || tx?.payment_intent_id);

  const { data, isLoading } = useGetList("Transaction", {
    filter:
      userCompanyId && customerUserId
        ? {
            user_id: customerUserId,
            order: {
              is: {
                location: {
                  is: {
                    company_id: userCompanyId
                  }
                }
              }
            }
          }
        : { order_id: id },
    sort: { field: "created_at", order: "DESC" },
    pagination: { page: 1, perPage: 1000 }
  });

  // let newData: any = [];

  // data?.forEach((item: any) => {
  //   if (item?.amount > 0 && !item?.total_tip_amount) {
  //     newData.push(item);
  //   } else if (item?.amount > 0 && item?.total_tip_amount > 0) {
  //     newData.push(item);
  //     // newData.push({
  //     //   ...item,
  //     //   id: item?.id + new Date().toString(),
  //     //   type: "Tips",
  //     //   amount: item?.total_tip_amount
  //     // });
  //   } else if (!item?.amount && item?.total_tip_amount > 0) {
  //     newData.push({
  //       ...item,
  //       type: "Tip Only",
  //       amount: item?.total_tip_amount
  //     });
  //   } else {
  //     newData.push(item);
  //   }
  // });
  const listContext = useList({ data: data, isLoading });

  const deleteOrder = async () => {
    try {
      await dataProvider.delete("Order", { id: id as string });
      navigate("/dashboard/Walk-in-order");
    } catch (err) {}
  };

  return (
    <div>
      <List
        filter={
          userCompanyId && customerUserId
            ? {
                user_id: customerUserId,
                order: {
                  is: {
                    location: {
                      is: {
                        company_id: userCompanyId
                      }
                    }
                  }
                }
              }
            : { order_id: id }
        }
        perPage={1000}
        resource="Transaction"
        empty={
          <div className="text-[14px] font-['Inter'] font-[400] text-mono-primaryTextColor">
            No transactions yet
          </div>
        }
        actions={
          <div className="h-0 relative">
            <div className="absolute -top-[50px] right-0 z-10 customListActionsButtonsWrapper">
              <ExportButton
                label={translate("common.export_to_csv")}
                icon={<DownloadIcon />}
              />
            </div>
          </div>
        }
        sx={{
          marginTop: "4px",
          "& .RaList-main > div": {
            minHeight: "0px"
          },
          "& .MuiTablePagination-root": {
            display: "none"
          },
          ".customListActionsButtonsWrapper .MuiButton-sizeSmall": {
            width: "max-content",
            height: "36px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#FAF9F7",
            color: "rgba(48, 47, 46, 0.95)",
            fontSize: "14px",
            lineHeight: "18px",
            fontWeight: 500,
            borderRadius: "18px",
            padding: "0px 16px",
            border: "1px solid rgba(48, 47, 46, 0.23)",
            textTransform: "none"
          }
        }}
      >
        <ListContextProvider value={listContext}>
          <CustomDataGrid
            rowClick={false}
            hover={false}
            sx={{
              borderRadius: "0px",
              ".RaDatagrid-headerCell": {
                backgroundColor: "transparent",
                minWidth: "0px",
                fontSize: "14px",
                "@media screen and (max-width:1280px)": {
                  fontSize: "12px"
                },
                "@media screen and (max-width:640px)": {
                  paddingLeft: "4px",
                  paddingRight: "4px"
                }
              },
              ".RaDatagrid-rowCell": {
                minWidth: "0px",
                "@media screen and (max-width:640px)": {
                  paddingLeft: "4px",
                  paddingRight: "4px"
                }
              },
              ".RaDatagrid-rowCell span": {
                fontSize: "14px",
                "@media screen and (max-width:1280px)": {
                  fontSize: "12px"
                }
              }
            }}
          >
            <FunctionField
              label={
                <span className="line-clamp-1 break-all">
                  {translate("order.method")}
                </span>
              }
              render={(record: any) => (
                <div className="capitalize line-clamp-1 break-all">
                  {
                    paymentMethodNames?.[
                      record?.payment_method as keyof typeof paymentMethodNames
                    ]
                  }
                </div>
              )}
            />
            <FunctionField
              label={
                <span className="line-clamp-1 break-all">
                  {translate("order.type")}
                </span>
              }
              render={(record: any) => (
                <div className="first-letter:capitalize line-clamp-1 break-all">
                  {record?.type?.replaceAll("_", " ")?.toLowerCase()}
                </div>
              )}
            />
            <FunctionField
              label={
                <span className="line-clamp-1 break-all">
                  {translate("order.status")}
                </span>
              }
              render={(record: any) => (
                <div className="capitalize line-clamp-1 break-all">
                  {record?.status?.toLowerCase()}
                </div>
              )}
            />
            <FunctionField
              label={
                <span className="line-clamp-1 break-all">
                  {translate("common.created_at")}
                </span>
              }
              render={(record: any) => (
                <div className="capitalize line-clamp-1 break-all">
                  {dayjs(record?.created_at).format("MM/DD/YYYY hh:mm A")}
                </div>
              )}
            />
            <FunctionField
              label={
                <span className="line-clamp-1 break-all">
                  {translate("order.tip")}
                </span>
              }
              textAlign="right"
              render={(record: any) => (
                <div>
                  {record?.total_tip_amount
                    ? formatCurrency(record?.total_tip_amount)
                    : "-"}
                </div>
              )}
            />
            <FunctionField
              label={
                <span className="line-clamp-1 break-all">
                  {translate("order.total_amount")}
                </span>
              }
              textAlign="right"
              render={(record: any) => (
                <div>{formatCurrency(record?.amount)}</div>
              )}
            />
            <FunctionField
              label=""
              textAlign="right"
              render={(record: any) =>
                isRefundableTransaction(record) ? (
                  <CustomButton
                    text={translate("order.refund")}
                    variant="secondary"
                    onClick={() => {
                      setRefundTransactionId(record?.id || null);
                      setIsRefundTransactionModalOpen(true);
                    }}
                    sx={{
                      height: "28px",
                      fontSize: "12px",
                      padding: "0 12px"
                    }}
                  />
                ) : null
              }
            />
          </CustomDataGrid>
        </ListContextProvider>
      </List>
      {!record?.appointment && deleteEnabled && data?.length === 0 && (
        <div className="w-full mt-3 flex justify-end">
          <CustomButton
            onClick={() => setIsConfirmDeleteOrder(true)}
            text={translate("common.delete")}
            sx={{
              backgroundColor: muiColors.error,
              "&:hover": {
                backgroundColor: muiColors.error,
                opacity: 0.9
              }
            }}
          />
        </div>
      )}
      {isConfirmDeleteOrder && (
        <ConfirmationModal
          open={!!isConfirmDeleteOrder}
          handleConfirm={deleteOrder}
          onClose={() => setIsConfirmDeleteOrder(false)}
          text="Are you sure you want to delete this order? This action can't be reversed, so please be certain before proceeding."
          buttonText="Delete"
        />
      )}
      {refundTransactionId && isRefundTransactionModalOpen && (
        <AddArbitaryCreditsModal
          isOpen={isRefundTransactionModalOpen}
          setIsOpen={setIsRefundTransactionModalOpen}
          refundTransactionId={refundTransactionId}
          setRefundTransactionId={setRefundTransactionId}
          refetch={() => Promise.resolve()}
          txItemRefetch={() => Promise.resolve()}
          txAddonRefetch={() => Promise.resolve()}
        />
      )}
    </div>
  );
}
